import React, { Component } from 'react';

import Pill from 'common/common/Pill';
import Tooltip from 'common/common/Tooltip';
import { cn } from 'common/style/utils';
import { P } from 'common/ui/Text';
import Button from 'common/uiV3/Button';
import ButtonLink from 'common/uiV3/Button/ButtonLink';
import Card from 'common/uiV3/Card';
import Flex from 'common/uiV3/Flex';
import VStack from 'common/uiV3/VStack';
import isNil from 'common/util/isNil';
import nbspLastSpace from 'common/util/nbspLastSpace';
import numberWithCommas from 'common/util/numberWithCommas';
import { Check } from 'lucide-react';
import PropTypes from 'prop-types';

export default class AdminBillingPlan extends Component {
  static propTypes = {
    breakdown: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        link: PropTypes.string,
        tooltip: PropTypes.string,
        value: PropTypes.string.isRequired,
      }),
    ),
    cta: PropTypes.string,
    description: PropTypes.string,
    disabled: PropTypes.bool,
    name: PropTypes.string,
    namePill: PropTypes.string,
    onSelect: PropTypes.func,
    price: PropTypes.number,
    priceDiscounted: PropTypes.number,
    priceOld: PropTypes.number,
    priceCustom: PropTypes.bool,
    selected: PropTypes.bool,
    selecting: PropTypes.bool,
    timeframe: PropTypes.oneOf(['mo', 'yr', 'mo (billed yearly)']),
  };

  static defaultProps = {
    timeframe: 'mo',
  };

  renderPrice() {
    const { price, priceCustom, priceDiscounted, priceOld, timeframe } = this.props;

    const clean = (number) => numberWithCommas(Math.ceil(number));
    if (!isNil(price)) {
      return (
        <div className="font-bold">
          ${clean(price)}/{timeframe}
        </div>
      );
    }

    if (priceCustom) {
      return <div className="font-bold">Custom</div>;
    }

    if (!isNil(priceOld) && !isNil(priceDiscounted)) {
      return [
        <div className="line-through" key="priceOld">
          ${clean(priceOld)}
        </div>,
        <div className="font-bold text-primary" key="priceDiscounted">
          ${clean(priceDiscounted)}/{timeframe}
        </div>,
      ];
    }
  }

  renderBreakdown() {
    const { breakdown } = this.props;

    if (!breakdown) {
      return;
    }

    return (
      <VStack className="mt-2 gap-1">
        <div className="font-bold">Pricing breakdown</div>
        {breakdown.map(({ label, link, tooltip, value }) => {
          return (
            <Flex className="text-secondary-foreground" key={label}>
              {link ? (
                <a
                  className="text-primary hover:underline"
                  href={link}
                  rel="noopener"
                  target="_blank">
                  {label}
                </a>
              ) : (
                label
              )}
              {tooltip && (
                <Tooltip value={tooltip}>
                  <div className="icon-help pl-1.5 flex items-center" />
                </Tooltip>
              )}
              : {value}
            </Flex>
          );
        })}
      </VStack>
    );
  }

  renderFooter() {
    const { cta, disabled, onSelect, selected, selecting } = this.props;

    if (selected) {
      return;
    }

    return (
      <Flex className="items-center mt-5 justify-between">
        <Button disabled={disabled} isLoading={selecting} onClick={onSelect}>
          {cta}
        </Button>
        <ButtonLink
          variant="link"
          color="secondary"
          href="https://canny.io/pricing"
          target="_blank">
          Learn more
        </ButtonLink>
      </Flex>
    );
  }

  render() {
    const { description, name, namePill, selected } = this.props;

    return (
      <Card
        size="large"
        className={cn('flex flex-col min-h-44 relative w-full box-border', {
          'border border-primary': selected,
        })}>
        <Flex className="justify-between">
          <Flex className="gap-2">
            <P fontWeight="bold">{name}</P>
            {namePill && <Pill>{namePill}</Pill>}
          </Flex>
          <div className="flex-grow" />
          {this.renderPrice()}
        </Flex>
        <div className="text-secondary-foreground mt-2.5">{nbspLastSpace(description)}</div>
        <div className="flex-grow" />
        {this.renderBreakdown()}
        {this.renderFooter()}
        {selected && (
          <Flex className="items-center justify-center absolute -right-2.5 -top-2.5 w-5 h-5 rounded-full bg-indigo-90">
            <Check size={14} className="stroke-3" />
          </Flex>
        )}
      </Card>
    );
  }
}
